import React, { useState, useEffect } from 'react'
import { Typography, Button, Stack, Box, Chip, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import { useTicketHistory } from '../hooks/useTicketHistory'
import { useScan } from '../context/scanContext'
import { ScanModes } from '../utils/scanModeEnum'
import AppShell from './layout/AppShell'
import { Surface } from './ui/Surface'
import { LoadingState, EmptyState } from './ui/States'

/**
 * Historique des transactions d'un billet.
 *
 * L'ancien tableau à 5 colonnes (source, heure, action, résultat, quantité)
 * se comprimait à ~60px par colonne sur un écran de 375px, avec des mots
 * coupés à la verticale. Chaque évènement est désormais une fiche empilée :
 * lisible sur n'importe quelle largeur, et la hiérarchie (action > détail >
 * métadonnées) guide l'œil au lieu de le forcer à balayer une grille.
 */
const TicketHistory = ({ onCancel }) => {
  const [isLoading, setIsLoading] = useState(true)
  const { currentCode, scanMode } = useScan()
  const { getTicketHistory, scanEvents } = useTicketHistory()

  useEffect(() => {
    getTicketHistory(
      currentCode,
      () => setIsLoading(false),
      () => setIsLoading(false)
    )
  }, [])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const offset = -4 // UTC-4

    const localDate = new Date(date.getTime() + offset * 60 * 60 * 1000)
    const day = String(localDate.getDate()).padStart(2, '0')
    const month = String(localDate.getMonth() + 1).padStart(2, '0')
    const year = localDate.getFullYear()
    const hours = String(localDate.getHours()).padStart(2, '0')
    const minutes = String(localDate.getMinutes()).padStart(2, '0')

    return `${day}/${month}/${year} ${hours}:${minutes}`
  }

  const isAccessMode = scanMode === ScanModes.ACCESS

  return (
    <AppShell
      title="Historique du billet"
      subtitle={isAccessMode ? "Contrôle d'accès" : 'Produits'}
      leading={
        <IconButton onClick={onCancel} aria-label="Retour" edge="start">
          <ArrowBackIcon />
        </IconButton>
      }
      footer={
        <Button fullWidth variant="contained" size="large" onClick={onCancel}>
          Retour
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState label="Récupération de l'historique" />
      ) : scanEvents.length === 0 ? (
        <EmptyState
          icon={<HistoryToggleOffIcon />}
          title="Aucune transaction"
          description="Ce billet n'a encore fait l'objet d'aucun scan."
        />
      ) : (
        <Stack spacing={1.25}>
          {scanEvents.map((row) => (
            <Surface key={row.Id} padding={1.75}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Typography variant="subtitle1" sx={{ minWidth: 0 }}>
                  {row.EventType}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', whiteSpace: 'nowrap', pt: 0.4 }}
                >
                  {formatDate(row.EventTime)}
                </Typography>
              </Stack>

              {row.Description ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {row.Description}
                </Typography>
              ) : null}

              <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                {row.Source ? <Chip size="small" variant="outlined" label={row.Source} /> : null}
                {!isAccessMode && row.Quantity != null ? (
                  <Chip size="small" color="primary" label={`Quantité : ${row.Quantity}`} />
                ) : null}
              </Stack>
            </Surface>
          ))}
          <Box sx={{ height: 8 }} />
        </Stack>
      )}
    </AppShell>
  )
}

export default TicketHistory
